/**
 * Offre après le définisseur de taille (sections/size-offer.liquid).
 *
 * Décision du principal, 2026-09-28 : au clic sur « Choisir le M », un code de
 * réduction valable quelques minutes, pour mesurer s'il manque une réduction ou
 * une urgence après la réflexion.
 *
 * Deux rôles, dans le même module :
 *
 * 1. Sur la fiche, `<size-offer>` ouvre la fenêtre, applique le code au panier
 *    et affiche le décompte.
 * 2. Sur toutes les pages (chargé par snippets/scripts.liquid), le module retire
 *    le code du panier à l'échéance. 🛑 C'est ce qui rend le décompte vrai : un
 *    décompte qui laisserait le code actif après zéro serait une fausse urgence.
 *    Limite connue : un client qui recopie le code peut le retaper au paiement,
 *    où le thème ne s'exécute pas. La limite d'une utilisation par client, dans
 *    l'admin, borne ce cas.
 *
 * L'état tient dans le navigateur du visiteur : une seule offre par visiteur,
 * refaire le définisseur ne relance ni la fenêtre ni le décompte.
 */
import { CartDiscountUpdateEvent, CartLinesUpdateEvent } from '@shopify/events';

const STORAGE_KEY = 'sinzem-size-offer';

/** @typedef {{ code: string, expiresAt: number, removed?: boolean }} OfferState */

/** @returns {OfferState | null} */
function readState() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

/** @param {OfferState} state */
function writeState(state) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    // Stockage indisponible (navigation privée stricte) : l'offre marche pour
    // cette page, sans mémoire d'une page à l'autre.
  }
}

const routes = () => window.Theme?.routes || {};
const sameCode = (/** @type {string} */ a, /** @type {string} */ b) => a.toUpperCase() === b.toUpperCase();

/**
 * Ajoute ou retire le code du panier, en gardant les autres codes, et prévient
 * le panier pour qu'il se redessine (assets/component-cart-items.js).
 *
 * Un code posé sur un panier vide y reste, inapplicable, et s'applique dès
 * qu'un article arrive : on peut donc l'ajouter avant l'ajout au panier.
 * @param {string} code
 * @param {boolean} present
 */
async function setCode(code, present) {
  const cart = await fetch(`${routes().cart_url || '/cart'}.js`).then((response) => response.json());
  /** @type {string[]} */
  const codes = (cart.discount_codes || []).map((/** @type {{ code: string }} */ discount) => discount.code);
  const has = codes.some((existing) => sameCode(existing, code));
  if (has === present) return;

  const next = present ? [...codes, code] : codes.filter((existing) => !sameCode(existing, code));
  const deferred = CartDiscountUpdateEvent.createPromise();

  document.dispatchEvent(
    new CartDiscountUpdateEvent({
      discountCodes: next.map((value) => ({ code: value })),
      promise: deferred.promise,
    })
  );

  try {
    const data = await fetch(`${routes().cart_update_url || '/cart/update'}.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ discount: next.join(',') }),
    }).then((response) => response.json());

    deferred.resolve?.({ cart: CartDiscountUpdateEvent.createCartFromAjaxResponse(data), detail: {} });
  } catch (error) {
    deferred.reject?.(error);
  }
}

/** Retire le code à l'échéance, une seule fois. */
async function expire() {
  const state = readState();
  if (!state || state.removed) return;

  await setCode(state.code, false).catch(() => {});
  writeState({ ...state, removed: true });
  renderCartTimers();
  document.dispatchEvent(new CustomEvent('size-offer:expired'));
}

/** @type {number | undefined} */
let expiryTimer;

/** @type {number | undefined} */
let cartTicker;

/**
 * Décompte à côté du code dans le panier (snippets/cart-summary.liquid) : la
 * pastille du code et la ligne du récapitulatif. Décision du principal,
 * 2026-09-28. Réécrit chaque seconde : le panier est redessiné par morph à
 * chaque changement, et les emplacements reviennent vides.
 */
function renderCartTimers() {
  const state = readState();
  const remaining = state ? state.expiresAt - Date.now() : 0;
  const active = Boolean(state && !state.removed && remaining > 0);

  for (const slot of document.querySelectorAll('[data-offer-code]')) {
    if (!(slot instanceof HTMLElement)) continue;
    const show = active && state !== null && sameCode(slot.dataset.offerCode || '', state.code);
    slot.hidden = !show;
    if (show) slot.textContent = formatRemaining(remaining);
  }

  if (!active) {
    window.clearInterval(cartTicker);
    cartTicker = undefined;
  }
}

/** Programme le retrait du code, ou le fait tout de suite s'il est échu. */
function scheduleExpiry() {
  const state = readState();
  if (!state || state.removed) return;

  window.clearTimeout(expiryTimer);
  const delay = state.expiresAt - Date.now();
  if (delay <= 0) {
    expire();
    return;
  }
  expiryTimer = window.setTimeout(expire, delay);

  renderCartTimers();
  if (cartTicker === undefined) cartTicker = window.setInterval(renderCartTimers, 1000);
}

/**
 * Pendant l'offre, chaque ajout au panier vérifie que le code y est toujours :
 * filet si le panier n'existait pas encore quand il a été posé.
 * @param {Event} event
 */
function ensureCodeAfterAdd(event) {
  const state = readState();
  if (!state || state.removed || Date.now() >= state.expiresAt) return;
  if (!(event instanceof CartLinesUpdateEvent) || !event.promise) return;

  event.promise.then(() => setCode(state.code, true)).catch(() => {});
}

/** « 4:59 » */
function formatRemaining(/** @type {number} */ milliseconds) {
  const total = Math.max(0, Math.ceil(milliseconds / 1000));
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`;
}

class SizeOffer extends HTMLElement {
  /** @type {number | undefined} */
  ticker;

  connectedCallback() {
    document.addEventListener('size-finder:apply', this.handleApply);
    document.addEventListener('size-offer:expired', this.render);
  }

  disconnectedCallback() {
    document.removeEventListener('size-finder:apply', this.handleApply);
    document.removeEventListener('size-offer:expired', this.render);
    window.clearInterval(this.ticker);
  }

  get dialogComponent() {
    return this.querySelector('dialog-component');
  }

  /**
   * Clic sur « Choisir le M ». Le définisseur ferme son dialogue juste après :
   * la fenêtre attend qu'il soit refermé pour s'ouvrir, deux dialogues
   * modaux ne se superposent pas proprement.
   * @param {Event} event
   */
  handleApply = (event) => {
    // Après la distribution de l'événement : un hôte qui garde la taille pour
    // lui l'annule, et le définisseur ne ferme alors rien.
    window.setTimeout(() => {
      if (event.defaultPrevented || readState()) return;

      const code = this.dataset.code || '';
      const minutes = parseInt(this.dataset.minutes || '', 10) || 5;
      if (!code) return;

      writeState({ code, expiresAt: Date.now() + minutes * 60_000 });
      setCode(code, true).catch(() => {});
      scheduleExpiry();

      // Clarity : filtrer les sessions où l'offre a été montrée.
      window.clarity?.('set', 'offre_taille', 'affichee');
      window.clarity?.('event', 'offre_taille_affichee');

      const guide = event.target instanceof Element ? event.target.closest('dialog-component') : null;
      let opened = false;
      const open = () => {
        if (opened) return;
        opened = true;
        this.open();
      };
      guide?.addEventListener('dialog:close', open, { once: true });
      // Filet si le dialogue du guide ne signale pas sa fermeture.
      window.setTimeout(open, 900);
    });
  };

  open() {
    this.render();
    this.ticker = window.setInterval(this.render, 1000);
    // @ts-ignore — DialogComponent (assets/dialog.js)
    this.dialogComponent?.showDialog?.();
  }

  render = () => {
    const state = readState();
    const timer = this.querySelector('[data-offer-timer]');
    const component = this.dialogComponent;
    if (!state || !(timer instanceof HTMLElement) || !(component instanceof HTMLElement)) return;

    const remaining = state.expiresAt - Date.now();
    const expired = state.removed || remaining <= 0;
    component.toggleAttribute('data-expired', expired);

    timer.textContent = expired
      ? this.dataset.expiredLabel || ''
      : (this.dataset.timerLabel || '{timer}').replace('{timer}', formatRemaining(remaining));

    if (expired) window.clearInterval(this.ticker);
  };
}

if (!customElements.get('size-offer')) {
  customElements.define('size-offer', SizeOffer);
  scheduleExpiry();
  document.addEventListener(CartLinesUpdateEvent.eventName, ensureCodeAfterAdd);
}
